import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Popover, Row, Tabs } from "antd";
import type { TabsProps } from "antd";
import { InputBox } from "../../../components/AntdAddons";
import { ReactComponent as UserIcon } from "../../../assets/images/icon/user.svg";
import { ReactComponent as PasswordIcon } from "../../../assets/images/icon/lock_final.svg";
import { assignErrorToInput } from "../../../store/api";
import { useSelector } from "react-redux";
import { AppDispatch, RootState, useAppDispatch } from "../../../store/app";
import { changePassword, updateProfile } from "../../../store/AuthSlice";
import { formRules } from "../../../config/validations/formRules";
import { rules } from "./rules";
import { useTranslation } from "react-i18next";

const Profile: React.FC<any> = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useAppDispatch();
  const userDetail = useSelector((state: RootState) => state.auth.userDetail);
  const [tooltip, setTooltip] = useState(false);
  const [form] = Form.useForm();
  const profileForm = useRef<any>();
  const updatePasswordForm = useRef<any>();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const passwordTooltip = (
    <div>
      <div>{t("GENERAL.PASSWORDTOOLTIP_AT_LEAST_1_NUMERIC_CHARACTER")}</div>
      <div>{t("GENERAL.PASSWORDTOOLTIP_AT_LEAST_1_SPECIAL_CHARACTER")}</div>
      <div>{t("GENERAL.PASSWORDTOOLTIP_AT_LEAST_1_UPPERCASE_LETTER")}</div>
      <div>{t("GENERAL.PASSWORDTOOLTIP_AT_LEAST_8_CHARACTER")}</div>
    </div>
  );
  useEffect(() => {
    form.setFieldsValue(userDetail);  
  }, [userDetail]);

  const onChange = () => {
    profileForm?.current
      ?.validateFields()
      .then(() => {
        setDisabled(false);
      })
      .catch((e: any) => {
        if (e.errorFields && e.errorFields.length > 0) {
          setDisabled(false);
        }
      });
  };

  const handleSubmit = (formDataValue: any) => {
    setSaving(true);
    const formData = { ...formDataValue, role_id: userDetail?.roles_id };
    dispatch(updateProfile(formData,userDetail?.id))
      .then(() => {
        setDisabled(true);
        setTooltip(false);
      })
      .catch((error: any) => {
        assignErrorToInput(profileForm.current, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  const onPasswordChange = () => {
    setTooltip(true);
    updatePasswordForm?.current
      ?.validateFields()
      .then(() => {
        setDisabled(false);
        setTooltip(false);
      })
      .catch((e: any) => {
        if (e.errorFields && e.errorFields.length > 0) {
          setDisabled(true);
        }
      });
  };

  const updatePassword = (data: any) => {
    setSaving(true);
    dispatch(changePassword(data))
      .then(() => {
        updatePasswordForm?.current?.resetFields();
        setDisabled(true);
      })
      .catch((error: any) => {
        assignErrorToInput(updatePasswordForm.current, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  // Add ref in form
  const items: TabsProps["items"] = [
    {
      key: "1",
      label: `${t("PROFILE.USER_PROFILE")}`,
      children: (
        <Form
          layout="vertical"
          autoComplete="off"
          ref={profileForm}
          form={form}
          id="publishForm"
          onFinish={handleSubmit}
          onChange={onChange}
          className="userProfile commanDrawer"
        >
          <Row className="iconRow">
            <div className="formIcon">
              <div className="borderIcon">
                <UserIcon />
              </div>
            </div>
          </Row>
          <Row gutter={15}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <InputBox.Text
                required
                label={t("PROFILE.FIRST_NAME")}
                name="name"
                rules={formRules.name()}
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <InputBox.Text
                required
                label={t("PROFILE.Last_NAME")}
                name="last_name"
                rules={formRules.name()}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputBox.Text
                name="email"
                label={t("GENERAL.EMAIL_ID")}
                // disabled={[1].includes(userDetail?.users_roles_id)}
                placeholder={t("GENERAL.EMAIL_PLACEHOLDER")}
                rules={formRules.email()}
                disabled={userDetail ? true : false}
              />
            </Col>
          </Row>
          <div className="profileBtn">
            <Button
              disabled={disabled}
              form="publishForm"
              loading={saving}
              htmlType="submit"
              type="primary"
              className={`${disabled ? "disableBtn" : "blackLineBtn"}`}
            >
              {t("GENERAL.SAVE_BUTTON")}
            </Button>
          </div>
        </Form>
      ),
    },
    {
      key: "2",
      label: `${t("PROFILE.CHANGE_PASSWORD_LABEL_LINK")}`,
      children: (
        <Form
          layout="vertical"
          autoComplete="off"
          ref={updatePasswordForm}
          id="changePass"
          onChange={onPasswordChange}
          onFinish={updatePassword}
        // initialValues={LAYOUT.initialValues}
        >
          <Row className="iconRow">
            <div className="formIcon">
              <div className="borderIcon">
                <PasswordIcon />
              </div>
            </div>
          </Row>
          <Row gutter={24}>
            {/* <Col span={24}>
              <InputBox.Password
                required
                name="old_password"
                label={t("PROFILE.OLD_PASSWORD")}
                placeholder={t("PROFILE.OLD_PASSWORD_PLACEHOLDER")}
                rules={formRules.password()}
              />
            </Col> */}
          </Row>
          <Row>
            <Col span={24}>
              <Popover
                placement="topRight"
                content={passwordTooltip}
                open={tooltip}
                align={{ offset: [0, -70] }}
              >
                <InputBox.Password
                  name="password"
                  required
                  label={t("PROFILE.NEW_PASSWORD")}
                  placeholder={t("PROFILE.NEW_PASSWORD_PLACEHOLDER")}
                  rules={rules.new_password()}
                />
              </Popover>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputBox.Password
                name="password_confirmation"
                label={t("GENERAL.CONFIRM_PASSWORD")}
                required
                placeholder={t("GENERAL.CONFIRM_PASSWORD_PLACEHOLDER")}
                rules={rules.confirm_password()}
              />
            </Col>
          </Row>
          <div className="profileBtn">
            <Button
              disabled={disabled}
              form="changePass"
              loading={saving}
              htmlType="submit"
              type="primary"
              className={`${disabled ? "disableBtn" : "blackLineBtn"}`}
            >
              {t("PROFILE.UPDATE_PASSWORD_BUTTON")}
            </Button>
          </div>
        </Form>
      ),
    },
  ];

  const onChangeTab = () => {
    setTooltip(false);
    setDisabled(false);
    setSaving(false);
  };

  return (
    <div className="profileLayout ">
      <Tabs
        defaultActiveKey="1"
        moreIcon={false}
        items={items}
        onChange={onChangeTab}
      />
    </div>
  );
};

export default Profile;
